package gr.tech.site.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.sections.ContactHeader
import gr.tech.site.components.sections.Footer
import gr.tech.site.components.sections.NavHeader
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

val PageContentStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Colors.White)
            .fillMaxSize()
    }
    Breakpoint.MD {
        Modifier
    }
}

class PageLayoutData(
    val showContactHeader: Boolean,
    val title: String,
    val description: String? = null,
    val canonicalUrl: String? = null,
    val ogImage: String? = null,
    val ogType: String = "website",
    val pageContentModifier: Modifier? = null
)

@Composable
@Layout
fun PageLayout(
    ctx: PageContext,
    content: @Composable ColumnScope.() -> Unit
) {
    val data = ctx.data.getValue<PageLayoutData>()
    LaunchedEffect(data.title) {
        document.title = "${data.title} | Green Rebates"
    }

    LaunchedEffect(data.description) {
        data.description?.let { desc ->
            val metaDescription = document.querySelector("meta[name='description']")
            metaDescription?.setAttribute("content", desc)
        }
    }

    LaunchedEffect(data.canonicalUrl) {
        data.canonicalUrl?.let { url ->
            val canonicalLink =
                document.querySelector("link[rel='canonical']")
                    ?: document.createElement("link").also {
                        it.setAttribute("rel", "canonical")
                        document.head!!.appendChild(it)
                    }
            canonicalLink.setAttribute("href", url)
        }
    }

    LaunchedEffect(data.title) {
        fun setOrCreateOgMeta(property: String, content: String) {
            val el = document.querySelector("meta[property='$property']")
                ?: document.createElement("meta").also {
                    it.setAttribute("property", property)
                    document.head!!.appendChild(it)
                }
            el.setAttribute("content", content)
        }

        val ogImageUrl = (data.ogImage ?: "/images/moneybackground.png").let {
            if (it.startsWith("http")) it else "https://greenrebates.com$it"
        }

        setOrCreateOgMeta("og:site_name", "Green Rebates")
        setOrCreateOgMeta("og:type", data.ogType)
        setOrCreateOgMeta("og:title", "${data.title} | Green Rebates")
        data.description?.let { setOrCreateOgMeta("og:description", it) }
        data.canonicalUrl?.let { setOrCreateOgMeta("og:url", it) }
        setOrCreateOgMeta("og:image", ogImageUrl)
    }

    Box(
        Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            // Create a box with two rows: the main content (fills as much space as it can) and the footer (which reserves
            // space at the bottom). "min-content" means the use the height of the row, which we use for the footer.
            // Since this box is set to *at least* 100%, the footer will always appear at least on the bottom but can be
            // pushed further down if the first row grows beyond the page.
            // Grids are powerful but have a bit of a learning curve. For more info, see:
            // https://css-tricks.com/snippets/css/complete-guide-grid/
            .gridTemplateRows { size(1.fr); size(minContent) },
        contentAlignment = Alignment.Center
    ) {
        Column(
            // Isolate the content, because otherwise the absolute-positioned SVG above will render on top of it.
            // This is confusing but how browsers work. Read up on stacking contexts for more info.
            // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_positioned_layout/Understanding_z-index/Stacking_context
            // Some people might have used z-index instead, but best practice is to avoid that if possible, because
            // as a site gets complex, Z-fighting can be a huge pain to track down.
            Modifier.fillMaxSize().gridRow(1),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Column(
                modifier = Modifier
                    .backgroundColor(Colors.White)
                    .fillMaxWidth()
                    .position(Position.Sticky)
                    .top(0.px)
                    .zIndex(10)
                    .borderBottom(1.px, LineStyle.Solid, Colors.LightGray),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (data.showContactHeader) {
                    ContactHeader()
                }
                NavHeader()
            }
            Div(PageContentStyle.toModifier()
                .then(
                    other = data.pageContentModifier ?: Modifier
                )
                .toAttrs()
            ) {
                content()
            }
        }
        // Associate the footer with the row that will get pushed off the bottom of the page if it can't fit.
        Footer(Modifier.fillMaxWidth().gridRow(2))
    }
}
